import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Skeleton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import EpisodeCardWithModals from 'components/card/EpisodeCardWithModals';
import EpisodeSoapSection from 'components/layouts/patients/EpisodeSoapSection';
import EvaluationCard from 'components/layouts/patients/EvaluationCard';
import EvaluationDetailModal from 'components/layouts/patients/EvaluationDetailModal';
import EvaluationFormModal from 'components/layouts/patients/EvaluationFormModal';
import Button from 'components/ui/Button';
import { useEpisode } from 'hooks/useEpisodes';
import { useEvaluationsByEpisode } from 'hooks/useEvaluations';
import { usePatient } from 'hooks/usePatients';
import React, { useState } from 'react';
import {
  MdAdd,
  MdArrowBack,
  MdFolderOff,
  MdNotes,
  MdScience,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { ClinicalEpisode, EstadoEpisodio, PhysicalEvaluation } from 'types/models';
import { useUser } from '@clerk/clerk-react';
import { getUserRole } from 'utils/auth';

function isActive(ep: ClinicalEpisode) {
  return ep.estado === EstadoEpisodio.ABIERTO || ep.estado === EstadoEpisodio.EN_TRATAMIENTO;
}

// ─── Evaluaciones panel (single episode) ──────────────────────────────────────

function EpisodeEvaluacionesPanel({
  episode,
  canWrite,
  isAdmin,
}: {
  episode: ClinicalEpisode;
  canWrite: boolean;
  isAdmin: boolean;
}) {
  const active = isActive(episode);
  const { data: evaluations = [], isLoading } = useEvaluationsByEpisode(
    episode.pacienteId,
    episode.id,
  );

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const [selected, setSelected] = useState<PhysicalEvaluation | null>(null);

  const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  const emptyBg    = useColorModeValue('gray.50', 'navy.800');

  return (
    <>
      <Box p='20px'>
        <Flex align='center' justify='space-between' mb='16px'>
          <Text fontSize='xs' fontWeight='800' color={mutedColor} textTransform='uppercase' letterSpacing='wider'>
            {isLoading ? '…' : `${evaluations.length} evaluación${evaluations.length !== 1 ? 'es' : ''}`}
          </Text>
          {canWrite && active && (
            <Button size='sm' colorScheme='teal' leftIcon={<Icon as={MdAdd} />} onClick={onCreateOpen}>
              Nueva Evaluación
            </Button>
          )}
        </Flex>

        {isLoading ? (
          <Flex direction='column' gap='8px'>
            {[1, 2, 3].map((i) => <Skeleton key={i} h='70px' borderRadius='12px' />)}
          </Flex>
        ) : evaluations.length === 0 ? (
          <Flex
            direction='column' align='center' justify='center'
            bg={emptyBg} borderRadius='16px' py='40px' gap='10px'>
            <Flex w='44px' h='44px' bg='teal.50' borderRadius='14px' align='center' justify='center'>
              <Icon as={MdScience} color='teal.400' w='20px' h='20px' />
            </Flex>
            <Text fontSize='sm' fontWeight='700' color={textColor}>Sin evaluaciones registradas</Text>
            <Text fontSize='xs' color={mutedColor} textAlign='center' maxW='260px'>
              {active
                ? 'Registra la primera evaluación física de este episodio'
                : 'No se registraron evaluaciones en este episodio'}
            </Text>
            {canWrite && active && (
              <Button size='sm' colorScheme='teal' mt='4px' leftIcon={<Icon as={MdAdd} />} onClick={onCreateOpen}>
                Agregar evaluación
              </Button>
            )}
          </Flex>
        ) : (
          <Flex direction='column' gap='8px'>
            {evaluations.map((ev) => (
              <EvaluationCard
                key={ev.id}
                evaluation={ev}
                onClick={() => { setSelected(ev); onDetailOpen(); }}
              />
            ))}
          </Flex>
        )}
      </Box>

      {canWrite && active && (
        <EvaluationFormModal isOpen={isCreateOpen} onClose={onCreateClose} episode={episode} />
      )}
      {selected && (
        <EvaluationDetailModal
          isOpen={isDetailOpen}
          onClose={() => { onDetailClose(); setSelected(null); }}
          evaluation={selected}
          episode={episode}
          canEdit={canWrite && active}
        />
      )}
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EpisodeDetailPage() {
  const { id: patientId, episodeId } = useParams<{ id: string; episodeId: string }>();
  const navigate  = useNavigate();
  const { user }  = useUser();
  const role      = getUserRole(user) ?? '';
  const isAdmin   = role === 'admin';
  const canWrite  = ['admin', 'medico', 'fisioterapeuta'].includes(role);

  const { data: patient,  isLoading: patientLoading  } = usePatient(patientId!);
  const { data: episode,  isLoading: episodeLoading  } = useEpisode(patientId!, episodeId!);

  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const mutedColor  = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const tabListBg   = useColorModeValue('gray.50', 'navy.750');

  const isLoading = patientLoading || episodeLoading;

  if (isLoading) {
    return (
      <Flex minH='60vh' align='center' justify='center'>
        <Spinner size='xl' color='brand.500' thickness='3px' />
      </Flex>
    );
  }

  if (!episode || !patient) {
    return (
      <Flex minH='60vh' align='center' justify='center' direction='column' gap='4'>
        <Icon as={MdFolderOff} w='40px' h='40px' color='gray.400' />
        <Text color='gray.400' fontWeight='600'>Episodio no encontrado</Text>
        <Button variant='ghost' leftIcon={<Icon as={MdArrowBack} />} onClick={() => navigate(`/admin/patients/${patientId}`)}>
          Volver al paciente
        </Button>
      </Flex>
    );
  }

  const patientName = `${patient.nombres} ${patient.apellidos}`;

  return (
    <Box minH='100vh' pt={{ base: '130px', md: '80px', xl: '80px' }}>

      {/* Breadcrumb */}
      <Flex align='center' gap='2' mb='24px'>
        <IconButton
          aria-label='Volver'
          icon={<Icon as={MdArrowBack} />}
          variant='ghost'
          size='sm'
          colorScheme='brand'
          onClick={() => navigate(`/admin/patients/${patientId}`)}
        />
        <Text color={mutedColor} fontSize='sm'>
          Pacientes /{' '}
          <Text
            as='span'
            fontWeight='600'
            cursor='pointer'
            _hover={{ textDecoration: 'underline' }}
            onClick={() => navigate(`/admin/patients/${patientId}`)}
          >
            {patientName}
          </Text>
          {' /'} <Text as='span' fontWeight='700' color='secondaryGray.900'>{episode.codigoHc}</Text>
        </Text>
      </Flex>

      {/* Layout 2 columnas */}
      <Grid
        templateColumns={{ base: '1fr', lg: '300px 1fr' }}
        gap='20px'
        alignItems='start'
      >
        {/* ── Panel izquierdo: info + acciones ── */}
        <GridItem>
          <Box position={{ base: 'static', lg: 'sticky' }} top='90px'>
            <EpisodeCardWithModals
              episode={episode}
              canWrite={canWrite}
              isAdmin={isAdmin}
            />
          </Box>
        </GridItem>

        {/* ── Panel derecho: tabs ── */}
        <GridItem>
          <Card p='0' overflow='hidden' borderColor={borderColor}>
            <Tabs variant='line' colorScheme='brand' isLazy>
              <TabList
                px='20px'
                borderBottom='1px solid'
                borderColor={borderColor}
                bg={tabListBg}
              >
                <Tab fontSize='sm' fontWeight='600' py='14px' px='16px' gap='6px'>
                  <Icon as={MdScience} w='15px' h='15px' />
                  Evaluaciones
                </Tab>
                <Tab fontSize='sm' fontWeight='600' py='14px' px='16px' gap='6px'>
                  <Icon as={MdNotes} w='15px' h='15px' />
                  Notas SOAP
                </Tab>
              </TabList>

              <TabPanels>
                {/* Evaluaciones */}
                <TabPanel p='0'>
                  <EpisodeEvaluacionesPanel
                    episode={episode}
                    canWrite={canWrite}
                    isAdmin={isAdmin}
                  />
                </TabPanel>

                {/* SOAP */}
                <TabPanel p='20px'>
                  <EpisodeSoapSection
                    episode={episode}
                    canWrite={canWrite}
                    isAdmin={isAdmin}
                    noBorder
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
}
